import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  FaShieldAlt, 
  FaEthereum, 
  FaHandshake, 
  FaGlobe, 
  FaLock, 
  FaChartLine,
  FaRegCreditCard
} from "react-icons/fa";
import { 
  MdSecurity, 
  MdOutlineHowToReg, 
  MdAttachMoney,
  MdSpeed
} from "react-icons/md";
import { GiWallet, GiProtectionGlasses } from "react-icons/gi";
import Navbar from "./NavBar";
import Footer from "./Footer";

// Placeholder for your logo - you'll need to replace this with your actual import path
const EscroniaLogo = () => (
  <div className="text-4xl font-extrabold bg-gradient-to-r from-green-400 to-blue-500 text-transparent bg-clip-text">
    ESCROWNIA
  </div>
);

const LandingPage = () => {
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    // Check if mobile device
    setIsMobileDevice(/Mobi|Android/i.test(navigator.userAgent));
    
    // Add scroll listener
    const handleScroll = () => {
      const offset = window.scrollY;
      if (offset > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Testimonials data
  const testimonials = [
    {
      name: "Alex Chen",
      role: "Freelance Developer",
      content: "Escrownia has completely transformed how I receive payments from international clients. The smart contract system ensures I always get paid for completed work.",
      stars: 5
    },
    {
      name: "Sarah Johnson",
      role: "Digital Asset Trader",
      content: "The security features are unmatched. I've tried other platforms but Escrownia's arbitration system gives me peace of mind for high-value transactions.",
      stars: 5
    },
    {
      name: "Michael Torres",
      role: "E-commerce Business Owner",
      content: "Our business has expanded globally thanks to Escrownia. Cross-border payments are now seamless and we've eliminated payment disputes entirely.",
      stars: 4
    }
  ];

  // Stats data
  const stats = [
    { label: "Transactions Protected", value: "$850M+" },
    { label: "Active Users", value: "125,000+" },
    { label: "Countries Served", value: "150+" },
    { label: "Dispute Resolution Rate", value: "99.8%" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 text-white">
      {/* Header */}

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="container mx-auto flex flex-col items-center text-center">
          <div className="mb-8">
            <EscroniaLogo />
          </div>
          <h1 className="text-5xl md:text-6xl font-extrabold mb-6 leading-tight max-w-4xl">
            <span className="bg-gradient-to-r from-green-400 to-blue-500 text-transparent bg-clip-text">
              Secure Transactions Guaranteed
            </span>
          </h1>
          <p className="text-xl opacity-90 max-w-3xl leading-relaxed mb-12">
            Welcome to <b>Escrownia</b>, the <b>next-generation blockchain-based escrow</b> platform designed to offer 
            <b> security</b>, <b>transparency</b>, and <b>efficiency</b> in financial transactions worldwide.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/register"
              className="px-8 py-4 rounded-lg text-lg font-semibold bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700 transition-all duration-300 shadow-lg"
            >
              Get Started Free
            </Link>
            <a
              href="#how-it-works"
              className="px-8 py-4 rounded-lg text-lg font-semibold border border-white hover:bg-white hover:text-gray-900 transition-all duration-300 shadow-lg"
            >
              How It Works
            </a>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-800 bg-opacity-50">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {stats.map((stat, index) => (
              <div key={index} className="p-6 rounded-lg bg-gradient-to-b from-gray-800 to-gray-900 shadow-xl">
                <div className="text-3xl md:text-4xl font-bold text-green-400 mb-2">{stat.value}</div>
                <div className="text-sm md:text-base text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-6">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Why Choose Escrownia</h2>
            <p className="text-xl opacity-80 max-w-3xl mx-auto">
              Our platform combines cutting-edge blockchain technology with user-friendly interfaces to provide the most secure escrow service available.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 max-w-6xl mx-auto">
            <div className="p-6 rounded-xl bg-gray-800 bg-opacity-50 border border-gray-700 hover:border-green-500 transition-all duration-300 shadow-xl">
              <div className="bg-gradient-to-br from-green-500 to-blue-600 w-16 h-16 rounded-lg flex items-center justify-center mb-6">
                <FaShieldAlt className="text-white text-2xl" />
              </div>
              <h3 className="text-2xl font-semibold mb-3">Tamper-Proof Security</h3>
              <p className="text-gray-400 leading-relaxed">
                Every transaction is <b>permanently recorded on the blockchain</b>, ensuring no alterations, fraud, or manipulation.
              </p>
            </div>

            <div className="p-6 rounded-xl bg-gray-800 bg-opacity-50 border border-gray-700 hover:border-green-500 transition-all duration-300 shadow-xl">
              <div className="bg-gradient-to-br from-green-500 to-blue-600 w-16 h-16 rounded-lg flex items-center justify-center mb-6">
                <MdSecurity className="text-white text-2xl" />
              </div>
              <h3 className="text-2xl font-semibold mb-3">Smart Contract Automation</h3>
              <p className="text-gray-400 leading-relaxed">
                Funds <b>remain in escrow</b> and are only released once <b>contract conditions</b> are met, ensuring trust between parties.
              </p>
            </div>

            <div className="p-6 rounded-xl bg-gray-800 bg-opacity-50 border border-gray-700 hover:border-green-500 transition-all duration-300 shadow-xl">
              <div className="bg-gradient-to-br from-green-500 to-blue-600 w-16 h-16 rounded-lg flex items-center justify-center mb-6">
                <FaHandshake className="text-white text-2xl" />
              </div>
              <h3 className="text-2xl font-semibold mb-3">Fair Dispute Resolution</h3>
              <p className="text-gray-400 leading-relaxed">
                If a conflict arises, our <b>trusted arbiters</b> step in to fairly resolve disputes, ensuring <b>both buyer and seller</b> are protected.
              </p>
            </div>

            <div className="p-6 rounded-xl bg-gray-800 bg-opacity-50 border border-gray-700 hover:border-green-500 transition-all duration-300 shadow-xl">
              <div className="bg-gradient-to-br from-green-500 to-blue-600 w-16 h-16 rounded-lg flex items-center justify-center mb-6">
                <GiWallet className="text-white text-2xl" />
              </div>
              <h3 className="text-2xl font-semibold mb-3">Stable Coin Transactions</h3>
              <p className="text-gray-400 leading-relaxed">
                Secure transactions with <b>USDT and USDC</b>, the most trusted stablecoins, <b>shielding you from price volatility</b>.
              </p>
            </div>

            <div className="p-6 rounded-xl bg-gray-800 bg-opacity-50 border border-gray-700 hover:border-green-500 transition-all duration-300 shadow-xl">
              <div className="bg-gradient-to-br from-green-500 to-blue-600 w-16 h-16 rounded-lg flex items-center justify-center mb-6">
                <MdSpeed className="text-white text-2xl" />
              </div>
              <h3 className="text-2xl font-semibold mb-3">Lightning Fast Transactions</h3>
              <p className="text-gray-400 leading-relaxed">
                Our optimized smart contracts ensure <b>quick verification</b> and <b>rapid fund transfers</b> once conditions are met.
              </p>
            </div>

            <div className="p-6 rounded-xl bg-gray-800 bg-opacity-50 border border-gray-700 hover:border-green-500 transition-all duration-300 shadow-xl">
              <div className="bg-gradient-to-br from-green-500 to-blue-600 w-16 h-16 rounded-lg flex items-center justify-center mb-6">
                <FaLock className="text-white text-2xl" />
              </div>
              <h3 className="text-2xl font-semibold mb-3">Multi-Layer Security</h3>
              <p className="text-gray-400 leading-relaxed">
                <b>2FA protection</b>, <b>wallet whitelisting</b>, and <b>private key encryption</b> ensure <b>maximum security</b> for your funds.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 px-6 bg-gray-800 bg-opacity-50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">How Escrownia Works</h2>
            <p className="text-xl opacity-80 max-w-3xl mx-auto">
              Our process is designed to be simple, secure, and transparent for all parties involved.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            <div className="relative">
              <div className="bg-gradient-to-br from-green-500 to-blue-600 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto z-20 relative">
                <span className="text-white text-xl font-bold">1</span>
              </div>
              <div className="absolute top-8 left-1/2 h-0.5 bg-green-500 w-full hidden md:block" style={{ transform: 'translateX(50%)' }}></div>
              <h3 className="text-xl font-semibold mb-3 text-center">Create Agreement</h3>
              <p className="text-gray-400 text-center">
                Define transaction terms, deadlines, and conditions for fund release.
              </p>
            </div>
            
            <div className="relative">
              <div className="bg-gradient-to-br from-green-500 to-blue-600 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto z-20 relative">
                <span className="text-white text-xl font-bold">2</span>
              </div>
              <div className="absolute top-8 left-1/2 h-0.5 bg-green-500 w-full hidden md:block" style={{ transform: 'translateX(50%)' }}></div>
              <h3 className="text-xl font-semibold mb-3 text-center">Fund Escrow</h3>
              <p className="text-gray-400 text-center">
                Buyer deposits funds that are secured in a smart contract.
              </p>
            </div>
            
            <div className="relative">
              <div className="bg-gradient-to-br from-green-500 to-blue-600 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto z-20 relative">
                <span className="text-white text-xl font-bold">3</span>
              </div>
              <div className="absolute top-8 left-1/2 h-0.5 bg-green-500 w-full hidden md:block" style={{ transform: 'translateX(50%)' }}></div>
              <h3 className="text-xl font-semibold mb-3 text-center">Delivery & Verification</h3>
              <p className="text-gray-400 text-center">
                Seller provides goods/services, buyer verifies completion.
              </p>
            </div>
            
            <div className="relative">
              <div className="bg-gradient-to-br from-green-500 to-blue-600 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto">
                <span className="text-white text-xl font-bold">4</span>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-center">Funds Released</h3>
              <p className="text-gray-400 text-center">
                Upon approval, funds are automatically transferred to the seller.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 px-6">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">What Our Users Say</h2>
            <p className="text-xl opacity-80 max-w-3xl mx-auto">
              Join thousands of satisfied users who trust Escrownia for their transactions.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="p-6 rounded-xl bg-gray-800 bg-opacity-50 border border-gray-700 shadow-xl">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className={`w-5 h-5 ${i < testimonial.stars ? 'text-yellow-400' : 'text-gray-600'}`} fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-gray-300 mb-6 italic">"{testimonial.content}"</p>
                <div>
                  <p className="font-semibold">{testimonial.name}</p>
                  <p className="text-sm text-gray-400">{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 bg-gradient-to-r from-green-600 to-blue-700">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Secure Your Transactions?</h2>
          <p className="text-xl opacity-90 max-w-3xl mx-auto mb-8">
            Join thousands of users worldwide who trust Escrownia for safe, transparent transactions.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/register"
              className="px-8 py-4 rounded-lg text-lg font-semibold bg-white text-gray-900 hover:bg-gray-200 transition-all duration-300 shadow-lg"
            >
              Create Free Account
            </Link>
            
            {isMobileDevice && (
              <a
                href="https://metamask.app.link/dapp/escrownia.com"
                className="px-8 py-4 rounded-lg text-lg font-semibold border-2 border-white hover:bg-white hover:text-gray-900 transition-all duration-300 shadow-lg flex items-center justify-center"
              >
                <FaEthereum className="mr-2 text-xl" />
                Open in MetaMask
              </a>
            )}
          </div>
        </div>
      </section>

    

      {/* Floating Chat Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <button className="bg-green-500 hover:bg-green-600 text-white w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all duration-300">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        </button>
      </div>

      {/* Trust Badges Section */}
      <section className="py-12 px-6 bg-gray-900">
        <div className="container mx-auto">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-semibold">Trusted & Secure</h3>
          </div>
          
          <div className="flex flex-wrap justify-center items-center gap-8">
            <div className="bg-gray-800 p-4 rounded-lg opacity-75 hover:opacity-100 transition-opacity">
              <div className="text-center">
                <div className="flex items-center justify-center h-12 mb-2">
                  <FaShieldAlt className="text-green-400 text-3xl" />
                </div>
                <p className="text-sm text-gray-400">SSL Encrypted</p>
              </div>
            </div>
            
            <div className="bg-gray-800 p-4 rounded-lg opacity-75 hover:opacity-100 transition-opacity">
              <div className="text-center">
                <div className="flex items-center justify-center h-12 mb-2">
                  <GiProtectionGlasses className="text-blue-400 text-3xl" />
                </div>
                <p className="text-sm text-gray-400">DDoS Protected</p>
              </div>
            </div>
            
            <div className="bg-gray-800 p-4 rounded-lg opacity-75 hover:opacity-100 transition-opacity">
              <div className="text-center">
                <div className="flex items-center justify-center h-12 mb-2">
                  <FaChartLine className="text-purple-400 text-3xl" />
                </div>
                <p className="text-sm text-gray-400">99.9% Uptime</p>
              </div>
            </div>
            
            <div className="bg-gray-800 p-4 rounded-lg opacity-75 hover:opacity-100 transition-opacity">
              <div className="text-center">
                <div className="flex items-center justify-center h-12 mb-2">
                  <FaEthereum className="text-yellow-400 text-3xl" />
                </div>
                <p className="text-sm text-gray-400">Blockchain Verified</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />

    </div>
  );
};

export default LandingPage;